import { Grid, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextFieldGrid";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import { Options } from "react-to-pdf";
import { getDefaultDashboardPdfFileName } from "../../SignalsUtils";

// Dialog component for saving the dashboard as a PDF
// toPDF function is passed in from the DashboardBody where the ref to the dashboard content exists
export const DashboardPdfDialog = observer(
    ({
        open,
        setOpen,
        toPDF,
    }: {
        open: boolean;
        setOpen: (open: boolean) => void;
        toPDF: (options?: Options) => Promise<void>;
    }) => {
        const currentDashboard = useCurrentDashboard();
        const [fileName, setFileName] = useState(
            currentDashboard
                ? getDefaultDashboardPdfFileName(currentDashboard.title)
                : "",
        );
        const [isExporting, setIsExporting] = useState(false);

        const handleCancel = useCallback(() => {
            if (currentDashboard?.isExportCaptureInProgress) return;

            currentDashboard?.cancelExport();
            setIsExporting(false);
            setOpen(false);
        }, [currentDashboard, setOpen]);

        const handleExport = useCallback(async () => {
            setIsExporting(true);
            try {
                await toPDF({ filename: fileName });
            } finally {
                setIsExporting(false);
                setOpen(false);
            }
        }, [toPDF, fileName, setOpen]);

        useEffect(() => {
            if (currentDashboard) {
                setFileName(
                    getDefaultDashboardPdfFileName(currentDashboard.title),
                );
            }
        }, [currentDashboard, open]);

        const exportPercentage = currentDashboard?.exportPercentage ?? 0;

        if (!currentDashboard) {
            return null;
        }

        return (
            <AcxDialog
                title={"Save as PDF"}
                subTitle={""}
                dialogContentChildren={
                    <Grid container style={{ width: "40vh" }} gap={1}>
                        <Grid item xs={12}>
                            <AcxMainTextField
                                id="save"
                                value={fileName}
                                onChange={(e) => {
                                    setFileName(e.target.value);
                                }}
                                labelText="File Name"
                            />
                        </Grid>
                        {isExporting && (
                            <Grid item xs={12}>
                                <Typography variant="caption" display="block">
                                    {`Preparing pdf... ${exportPercentage}%`}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                }
                isOpen={open}
                onClose={handleCancel}
                disableEnforceFocus
                buttonsDisabled={currentDashboard.isExportCaptureInProgress}
                disableBackdropClose={
                    currentDashboard.isExportCaptureInProgress
                }
                children={
                    <Grid container item xs={4}>
                        <AcxButton
                            fullWidth
                            color="primary"
                            onClick={handleExport}
                            loading={isExporting}
                            containerStyle={{
                                width: "415px",
                            }}
                        >
                            Save as PDF
                        </AcxButton>
                    </Grid>
                }
            />
        );
    },
);
